import { useApolloClient } from "@apollo/client";
import { LabeledValue } from "@msys/ui";
import { Stack } from "@mui/material";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import React from "react";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { CategoriesInput } from "../../categories/CategoriesInput.js";
import { CategoriesLabeledValue } from "../../categories/CategoriesLabeledValue.js";
import { SectionCard } from "../sections/SectionCard.js";
import {
  OrganisationDetailsServicesBox_ViewerOrganisationFragment,
  useOrganisationDetailsServicesBox_ModifyOrganisationMutation,
} from "./OrganisationDetailsServicesBox.generated.js";
import { useTranslate } from "@tolgee/react";

interface Props {
  organisation: OrganisationDetailsServicesBox_ViewerOrganisationFragment;
  isViewerOrganisation: boolean;
}

interface ServicesOfferedFormValues {
  serviceCategories: string[];
  serviceArea: string;
}

function ServicesOfferedForm({ organisation, isViewerOrganisation }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const { t } = useTranslate(["OrganisationProfile"]);

  const [modifyOrganisation] =
    useOrganisationDetailsServicesBox_ModifyOrganisationMutation({
      client,
      refetchQueries: [namedOperations.Query.OrganisationDetails],
    });

  const initialValues: ServicesOfferedFormValues = React.useMemo(
    () => ({
      serviceCategories: organisation.categories || [],
      serviceArea: organisation.serviceArea,
    }),
    [organisation.categories, organisation.serviceArea]
  );

  const handleSubmit = async (values: ServicesOfferedFormValues) => {
    try {
      const input = {
        ...(isViewerOrganisation ? {} : { organisationId: organisation.id }),
        categories: values.serviceCategories,
        serviceArea: values.serviceArea,
      };

      await modifyOrganisation({
        variables: { input },
      });
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    }
  };

  return (
    <Formik<ServicesOfferedFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {formikProps => (
        <Form>
          <Stack spacing={1}>
            <CategoriesInput
              categoryIds={formikProps.values.serviceCategories}
              onChange={async categories => {
                await formikProps.setFieldValue(
                  "serviceCategories",
                  categories.map(c => c.id)
                );
              }}
              onClear={async () => {
                await formikProps.setFieldValue("serviceCategories", []);
              }}
              label={t("Services offered", { ns: "OrganisationProfile" })}
              disabled={false}
              alwaysShowLabel
            />
            <TextField
              name="serviceArea"
              label={t("Service area", { ns: "OrganisationProfile" })}
              multiline
            />
            <AutoSave />
          </Stack>
        </Form>
      )}
    </Formik>
  );
}

function ServicesOfferedView({
  organisation,
}: {
  organisation: OrganisationDetailsServicesBox_ViewerOrganisationFragment;
}) {
  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  return (
    <Stack spacing={1}>
      <CategoriesLabeledValue
        label={t("Services offered", { ns: "OrganisationProfile" })}
        categoryIds={organisation.categories || []}
      />
      <LabeledValue label={t("Service area", { ns: "OrganisationProfile" })}>
        {organisation.serviceArea || t("Not set", { ns: "Global" })}
      </LabeledValue>
    </Stack>
  );
}

export function OrganisationDetailsServicesBox({
  organisation,
  isViewerOrganisation,
}: Props) {
  const { t } = useTranslate(["OrganisationProfile"]);
  const viewer = useUserData().currentUser!;

  return (
    <SectionCard title={t("Services offered", { ns: "OrganisationProfile" })}>
      <RestrictedByOrganisationPermissionWithDebug
        permission="MANAGE_ORG"
        otherwise={<ServicesOfferedView organisation={organisation} />}
      >
        {viewer.organisation.id === organisation.id &&
        !!viewer.roles.find(r => r.internalName === "ORG_ADMIN") ? (
          <ServicesOfferedForm
            organisation={organisation}
            isViewerOrganisation={isViewerOrganisation}
          />
        ) : (
          <ServicesOfferedView organisation={organisation} />
        )}
      </RestrictedByOrganisationPermissionWithDebug>
    </SectionCard>
  );
}
